import { cn } from "@/lib/utils/index";
import { countWords } from "@/lib/utils/word-count";
import { forwardRef, useEffect, useId, useRef } from "react";

interface FetchTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  helperText?: string;
  wordLimit?: number;
  value?: string;
  maxHeight?: number;
  noAutoResize?: boolean; // Disable auto-resize, content will be scrollable
  /**
   * Let the user drag-resize the textarea vertically. Implies no JS auto-resize
   * (a manual drag would otherwise be reset on the next keystroke) and an
   * internal scrollbar once content exceeds the chosen height.
   */
  resizable?: boolean;
  /**
   * Fill the height of a flex parent instead of sizing to `rows`/content, with the text scrolling
   * internally. Implies no JS auto-resize and ignores `maxHeight`. The parent must give this a
   * bounded height (a `flex-1 min-h-0` chain); use inside a `flex flex-col` container.
   */
  fill?: boolean;
}

/**
 * Nowadays themed text area.
 */
export const FetchTextArea = forwardRef<HTMLTextAreaElement, FetchTextAreaProps>(
  (
    {
      className,
      error,
      helperText,
      disabled,
      wordLimit,
      value = "",
      maxHeight,
      noAutoResize = false,
      resizable = false,
      fill = false,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref
  ) => {
    const safeValue = value ?? "";
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    // Announce + associate the helper/error text so screen readers link it to the textarea.
    const helperId = useId();
    const wordCountId = useId();

    // Combine refs
    const combinedRef = (node: HTMLTextAreaElement) => {
      (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }
    };

    // Auto-resize textarea (skip when disabled, drag-resized, or filling a flex parent)
    useEffect(() => {
      if (fill || noAutoResize || resizable) return;

      const textarea = textareaRef.current;
      if (!textarea) return;

      const adjustHeight = () => {
        textarea.style.height = "auto";
        const scrollHeight = textarea.scrollHeight;
        const minHeight = 80;

        if (maxHeight) {
          const targetHeight = Math.min(scrollHeight, maxHeight);
          textarea.style.height = `${Math.max(targetHeight, minHeight)}px`;
        } else {
          textarea.style.height = `${Math.max(scrollHeight, minHeight)}px`;
        }
      };

      adjustHeight();
    }, [safeValue, maxHeight, noAutoResize, resizable, fill]);

    // Calculate word count. Shared helper so a caller gating its submit button on the same
    // count can never disagree with the readout rendered below.
    const wordCount = countWords(safeValue);
    const isOverLimit = wordLimit ? wordCount > wordLimit : false;
    // The helper text (an error or a hint) and the over-limit word count are distinct concerns:
    // both are associated for description, but only `error` drives the assertive alert styling below.
    const describedBy =
      [ariaDescribedBy, helperText ? helperId : undefined, isOverLimit ? wordCountId : undefined]
        .filter(Boolean)
        .join(" ") || undefined;

    // Render
    return (
      <div className={fill ? "flex min-h-0 w-full flex-1 flex-col" : "w-full"}>
        <textarea
          ref={combinedRef}
          className={cn(
            "block touch-manipulation leading-normal placeholder:leading-normal text-gray-800 font-medium min-h-[80px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-base sm:text-sm",
            resizable ? "resize-y" : "resize-none",
            fill ? "min-h-0 flex-1" : "",
            "focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 focus:outline-none transition-all duration-200",
            "focus:shadow-[0_0_0_3px_rgba(165,180,252,0.2)]",
            error || isOverLimit ? "border-red-400 focus:border-red-500 focus:ring-red-200 focus:shadow-none" : "",
            disabled ? "cursor-not-allowed opacity-50" : "",
            className
          )}
          style={
            fill
              ? // Height comes from the flex parent; text scrolls internally.
                { overflowY: "auto" }
              : resizable
                ? // User controls height via the drag handle; rows sets the initial
                  // height and content scrolls internally past it.
                  { maxHeight: maxHeight ? `${maxHeight}px` : undefined, overflowY: "auto" }
                : {
                    height: "auto",
                    maxHeight: maxHeight ? `${maxHeight}px` : "none",
                    overflowY: maxHeight ? "auto" : "visible",
                  }
          }
          disabled={disabled}
          value={safeValue}
          aria-invalid={ariaInvalid ?? (error || isOverLimit ? true : undefined)}
          aria-describedby={describedBy}
          {...props}
        />
        {(helperText || wordLimit) && (
          <div className="mt-1 flex justify-between items-center">
            {helperText && (
              <p
                id={helperId}
                role={error ? "alert" : undefined}
                className={cn("text-sm", error ? "text-red-600" : "text-gray-500")}
              >
                {helperText}
              </p>
            )}
            {wordLimit && (
              <p
                id={wordCountId}
                // Polite (not assertive) so the running counter doesn't re-interrupt the screen
                // reader on every keystroke past the limit; the error helper keeps role="alert".
                role={isOverLimit ? "status" : undefined}
                className={cn("text-sm ml-auto font-medium", isOverLimit ? "text-red-600" : "text-gray-500")}
              >
                {wordCount}/{wordLimit} words
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

FetchTextArea.displayName = "FetchTextArea";
